import React, { useState } from "react";
import { Form, Container, Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";
import AppButton from "../../Components/MerchantButton";

function ThermalRollDeliveryPage({ primaryDetails, setPrimaryDetails, navigation }) {
 const [isUploading, setIsUploading] = useState(false);
 const [errors, setErrors] = useState({});

  const handlePhotoChange = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      setPrimaryDetails(prev => ({
        ...prev,
        [`${fieldName}_temp_path`]: uploadResult.temp_path
      }));
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "file") return; // photos handled by handlePhotoChange
    setPrimaryDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const validate = () => {
    const newErrors = {
      thermal_roll_no: !primaryDetails.thermal_roll_no,
      thermal_roll_photo: !primaryDetails.thermal_roll_photo_temp_path,
      thermal_roll_delivery_receipt: !primaryDetails.thermal_roll_delivery_receipt_temp_path,
    };

    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some(Boolean);

    if (hasErrors) {
      toast.error("Please fill in all required fields", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validate()) {
      navigation.go('charger-delivery');
    }
  };

  const handleBack = () => {
    navigation.previous();
  };

  // Check if form is valid for visual feedback
  const isFormValid = () => {
    return primaryDetails.thermal_roll_no &&
           primaryDetails.thermal_roll_photo_temp_path &&
           primaryDetails.thermal_roll_delivery_receipt_temp_path;
  };

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">

          {/* Main Form */}
          <Col md={10}>
              <div className="form-main-header">
                <h4 className="form-title">THERMAL ROLL DELIVERY</h4>
                <p className="form-desc">Please provide thermal roll delivery information</p>
              </div>
              <div className="form-main">
              <div className="form-main-content">
                <Form>
                  {/* Thermal Roll Escalation No. */}
                  <Form.Group className="mb-4" controlId="thermal_roll_no">
                    <Form.Label className="form-label">
                      Thermal Roll Escalation No. <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="thermal_roll_no"
                      value={primaryDetails.thermal_roll_no || ''}
                      onChange={handleChange}
                      placeholder="Enter escalation number..."
                      className={`form-input${errors.thermal_roll_no ? " form-input-error" : ""}`}
                    />
                    {errors.thermal_roll_no && (
                      <div className="form-error">
                        This field is required
                      </div>
                    )}
                    <div>
                      <p className="form-note-text">
                        <em>"Note: Can be Escalation from Maya App Help Center or by Email."</em>
                      </p>
                    </div>
                  </Form.Group>
                  {/* Thermal roll escalation request photo */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Thermal roll escalation request photo <span className="form-required">*</span>
                      {primaryDetails.thermal_roll_photo_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Text className="form-hint">
                      Upload a photo showing the escalation request
                    </Form.Text>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoChange(e, 'thermal_roll_photo')}
                      isInvalid={errors.thermal_roll_photo}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {errors.thermal_roll_photo && (
                      <div className="form-error">Required</div>
                    )}
                  </Form.Group>

                  {/* Signed thermal roll delivery receipt */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Signed thermal roll delivery receipt <span className="form-required">*</span>
                      {primaryDetails.thermal_roll_delivery_receipt_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Text className="form-hint">
                      Upload a photo of the signed delivery receipt
                    </Form.Text>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoChange(e, 'thermal_roll_delivery_receipt')}
                      isInvalid={errors.thermal_roll_delivery_receipt}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {errors.thermal_roll_delivery_receipt && (
                      <div className="form-error">Required</div>
                    )}
                  </Form.Group>

                  {/* Navigation Buttons */}
                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                    >
                       Next →
                    </AppButton>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ThermalRollDeliveryPage;
